import { type FormEvent, useState } from "react";
import classes from "~/features/CADSearch/SchaefflerCADFileSearchComponent.module.less";
import SearchInput from "~/features/search/components/Autosuggest/SearchInput";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { useFetchData } from "~/features/search/components/Autosuggest/SearchBox";
import { Button } from "~/common/mui";
import CenterContents from "~/common/components/CenterContents";
import NullableImage from "~/common/components/NullableImage";
import ProductPreferredIndicator from "~/features/productDetails/components/ProductPreferredIndicator";
import { getDesignationAbbreviation } from "~/domains/product/utils/designationAbbreviation";
import { Translation } from "~/app/i18n/Translation";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { MEDIA_QUERY_MAX_PHONE } from "~/common/utils/screenSizes";
import { SEARCH_PAGE } from "~/utils/url/staticUrls";
import { useTranslation } from "react-i18next";
import { redirectToUrl } from "~/utils/url/urlUtils";

export function SchaefflerCADFileSearchComponent() {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const { val: suggestionsVisible, setFalse: hideSuggestions, setTrue: showSuggestions } = useBooleanState(false);

  const data = useFetchData(text, true);
  const isMaxMobile = useMediaQuery(MEDIA_QUERY_MAX_PHONE);
  const products = isMaxMobile ? data.data?.products.slice(0, 4) ?? [] : data.data?.products ?? [];
  const hasMoreResults = !!(data.data && data.data.totalResults && data.data.totalResults > data.data.products.length);

  const searchUrl = SEARCH_PAGE + `?text=${text.trim()}&sort=relevance&initialTabTabular=true`;
  let submitUrl: string;
  if (products.length === 1) {
    submitUrl = products[0].cadUrl ?? searchUrl;
  } else {
    submitUrl = searchUrl;
  }

  const clearInput = () => setText("");

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    hideSuggestions();
    redirectToUrl(submitUrl);
  };

  return (
    <div className={classes.container}>
      <div className={classes.image} />
      <div className={classes.content}>
        <div className={classes.title}>{t("cadTaskForce.title")}</div>
        <div className={classes.description}>{t("cadTaskForce.description")}</div>
        <div className={classes.input}>
          <form onSubmit={onSubmit}>
            <SearchInput
              variant={"cad"}
              value={text}
              onChange={setText}
              onFocus={showSuggestions}
              onClear={clearInput}
              placeholder={t("cadTaskForce.placeholder")}
            />
          </form>

          {data.state === "loaded" && products.length > 0 && suggestionsVisible && (
            <div className={classes.results}>
              {products.map((product, index) => (
                <a className={classes.resultItem} href={product.cadUrl} key={index}>
                  <CenterContents className={classes.resultItemImage}>
                    <NullableImage data={product.primaryImage} />
                  </CenterContents>
                  <div className={classes.resultItemContent}>
                    <div className={"d-flex"}>
                      {product.preferred && <ProductPreferredIndicator className={"icon-margin"} />}
                      <span className={classes.resultItemDesignation}>
                        {getDesignationAbbreviation(product.preferred ? 16 : 20)(product.designation)}
                      </span>
                    </div>
                    <p className="my-1 f-carbon">
                      <Translation {...product.shortDescription} />
                    </p>
                  </div>
                </a>
              ))}
              {hasMoreResults && (
                <Button className={classes.button} fullWidth={true} href={searchUrl}>
                  {t("cadTaskForce.button.text")}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function createSchaefflerCADFileSearchComponent() {
  return <SchaefflerCADFileSearchComponent />;
}
